import type { Conversation, InboxFilter } from '../types/whatsapp';

// The list on screen is filtered (tab, "solo sin responder", search) and paged, but
// inbox/changes returns the changed conversations unfiltered. These functions apply
// the same rules the server uses so the list stays right between full reloads.

export interface ListView {
  filter: InboxFilter;
  unanswered: boolean;
  q: string;
}

/** True when a conversation belongs in the list for this tab and switch (not the search). */
export const matchesView = (c: Conversation, view: ListView): boolean =>
  (view.filter === 'all' || c.assigned_to === view.filter) && (!view.unanswered || c.awaiting_reply);

const time = (iso: string | null): number => (iso ? Date.parse(iso) : Number.NEGATIVE_INFINITY);

/** Server order: urgent first, then the most recent message, then the newest id. Negative = a first. */
export const compareConversations = (a: Conversation, b: Conversation): number => {
  if (a.urgent !== b.urgent) return a.urgent ? -1 : 1;
  const ta = time(a.last_message_at);
  const tb = time(b.last_message_at);
  if (ta !== tb) return ta > tb ? -1 : 1;
  return b.id - a.id;
};

export interface MergeResult {
  items: Conversation[];
  /** The list must be fetched again (a search is active: the server's accent-insensitive match can't be reproduced here). */
  needsRefetch: boolean;
}

/**
 * Applies a batch of changed conversations to the loaded page.
 *  - a stale copy (older updated_at than the one held) is ignored;
 *  - a conversation that no longer matches the tab or switch leaves the list;
 *  - a new row that would sort after the last loaded one belongs to a page not loaded yet, so it is skipped;
 *  - with a search active nothing is merged: the caller refetches.
 */
export const mergeConversations = (
  items: Conversation[],
  changed: Conversation[],
  view: ListView,
  hasMore: boolean,
): MergeResult => {
  if (changed.length === 0) return { items, needsRefetch: false };
  if (view.q.trim() !== '') return { items, needsRefetch: true };

  const byId = new Map(items.map((c) => [c.id, c]));
  for (const c of changed) {
    const held = byId.get(c.id);
    if (held && Date.parse(held.updated_at) > Date.parse(c.updated_at)) continue;
    if (!matchesView(c, view)) {
      byId.delete(c.id);
      continue;
    }
    if (!held && hasMore && byId.size > 0) {
      const sorted = [...byId.values()].sort(compareConversations);
      const last = sorted[sorted.length - 1];
      if (compareConversations(c, last) > 0) continue;
    }
    byId.set(c.id, c);
  }
  return { items: [...byId.values()].sort(compareConversations), needsRefetch: false };
};
